import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PrimarySecondaryPage } from "@wayflyer/flyui";
import { OnboardingStepper } from "onboarding";
import { useIntl } from "react-intl";
import { getAIRsDataQueryOptions, getInReviewDetailsQueryKey } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { ConnectionAuthCallbackHandler } from "../../components";
import { FUNDING_APPLICATION_CONNECTORS_API, NAVIGATION_PATHS } from "../../components/constants";
import { platformConnectorsQueryParams, useHandleAirConnection } from "../../hooks/air-connection";
import { useInReviewDetails } from "../../queries/review-screen/review-screen-queries";

import { MESSAGES } from "./message-definitions";
import { ReviewApplicationProgress } from "./primary-content/review-application-progress";

export const AirsReviewPage = () => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const queryClient = useQueryClient();

  const { handleSuccess: handleAirConnectionSuccess, handleError: handleAirConnectionError } = useHandleAirConnection({
    airBasePath: NAVIGATION_PATHS.ADDITIONAL_INFORMATION_REQUEST,
  });

  return (
    <ConnectionAuthCallbackHandler
      onSuccess={async (args) => {
        await queryClient.invalidateQueries({
          queryKey: [
            FUNDING_APPLICATION_CONNECTORS_API.replace(":customerSlug", customerDomain),
            platformConnectorsQueryParams,
          ],
        });
        await handleAirConnectionSuccess(args);
        await queryClient.invalidateQueries({
          queryKey: getInReviewDetailsQueryKey(customerDomain),
        });
      }}
      onError={handleAirConnectionError}
    >
      <AirsReviewPageContent />
    </ConnectionAuthCallbackHandler>
  );
};

function AirsReviewPageContent() {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: reviewData, isFetchedAfterMount: isReviewDataFetched } = useInReviewDetails(customerDomain);
  const { data: airsData, isFetchedAfterMount: airsDataFetched } = useQuery({
    ...getAIRsDataQueryOptions(customerDomain),
  });
  const isReady = !!(airsData && airsDataFetched && isReviewDataFetched && reviewData);

  const titleSlot = !isReady || reviewData.isSubsequentFunding ? null : <OnboardingStepper currentStep={5} />;

  return (
    <>
      {isReady && (
        <PrimarySecondaryPage
          titleSlot={titleSlot}
          showStepperBackground={!!titleSlot}
          title={intl.formatMessage(MESSAGES.airReviewPageHeading)}
          titleWrap="wrap"
          primaryContent={<ReviewApplicationProgress airsData={airsData} />}
        />
      )}
    </>
  );
}
